import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Collapsible from "components/ui/collapsible/Collapsible";
import Input from "components/ui/input/Input";
import TextAreaInput from "components/ui/input/TextAreaInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select/Select";
import toast from "components/ui/toast/Toast";
import Toggle from "components/ui/toggle/Toggle";
import { authBaseUrl } from "lib/auth/authClient";
import {
  digestAlgorithm,
  identifierFormat,
  identifierFormatLabels,
  type SamlConfigInput,
  samlFormCreateSchema,
  samlFormSchema,
  type SamlFormValues,
  signatureAlgorithm,
} from "lib/hooks/api/auth/sso/schemas";
import { useRegisterSsoProvider } from "lib/hooks/api/auth/sso/useRegisterSsoProvider";
import { useUpdateSsoProvider } from "lib/hooks/api/auth/sso/useUpdateSsoProvider";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import type { ProviderPreset } from "./ProviderSelector";
import {
  extractEmailDomain,
  generateProviderId,
  generateSamlEntityId,
  pickAndReadPemFile,
  wrapBareBase64AsPem,
} from "./ProviderUtils";
import { CopyableField, ErrorText, FieldGroup, Form, TagInput, ToggleRow } from "./SharedStyles";

type SamlCertificateInfo =
  | {
      fingerprintSha256: string;
      notBefore: string;
      notAfter: string;
      publicKeyAlgorithm: string;
    }
  | { error: string };

type SamlEditProvider = {
  providerId: string;
  issuer: string;
  domain: string;
  samlConfig?: {
    entryPoint?: string;
    wantAssertionsSigned?: boolean;
    authnRequestsSigned?: boolean;
    signatureAlgorithm?: string;
    digestAlgorithm?: string;
    identifierFormat?: string;
    certificate?: SamlCertificateInfo[];
  };
};

function formValuesToSamlConfig(values: SamlFormValues): SamlConfigInput;
function formValuesToSamlConfig(values: SamlFormValues, _isEditing: true): Partial<SamlConfigInput>;
function formValuesToSamlConfig(
  values: SamlFormValues,
  _isEditing?: boolean,
): Partial<SamlConfigInput> {
  const isMetadataMode = values.configMode === "metadata";

  function buildIdpMetadata() {
    if (isMetadataMode) {
      if (!values.idpMetadataXml) return undefined;
      return { metadata: values.idpMetadataXml };
    }
    if (!values.idpEntityId) return undefined;
    return { entityID: values.idpEntityId };
  }

  const idpMetadata = buildIdpMetadata();

  return {
    entryPoint: values.entryPoint,
    ...(values.cert && { cert: values.cert }),
    ...(idpMetadata && { idpMetadata }),
    ...(values.wantAssertionsSigned !== undefined && {
      wantAssertionsSigned: values.wantAssertionsSigned,
    }),
    ...(values.authnRequestsSigned !== undefined && {
      authnRequestsSigned: values.authnRequestsSigned,
    }),
    ...(values.privateKey && { privateKey: values.privateKey }),
    signatureAlgorithm: values.signatureAlgorithm,
    digestAlgorithm: values.digestAlgorithm,
    identifierFormat: values.identifierFormat,
    ...buildMapping(values),
  };
}

function buildMapping(values: SamlFormValues) {
  const hasAny =
    values.mappingEmail || values.mappingName || values.mappingFirstName || values.mappingLastName;
  if (!hasAny) return {};

  return {
    mapping: {
      email: values.mappingEmail || "email",
      name: values.mappingName || "displayName",
      ...(values.mappingFirstName && { firstName: values.mappingFirstName }),
      ...(values.mappingLastName && { lastName: values.mappingLastName }),
    },
  };
}

function samlConfigToFormDefaults(
  config?: SamlEditProvider["samlConfig"],
): Partial<SamlFormValues> {
  if (!config) return {};
  return {
    ...(config.entryPoint != null && { entryPoint: config.entryPoint }),
    ...(config.wantAssertionsSigned != null && {
      wantAssertionsSigned: config.wantAssertionsSigned,
    }),
    ...(config.authnRequestsSigned != null && { authnRequestsSigned: config.authnRequestsSigned }),
    ...(config.signatureAlgorithm != null && {
      signatureAlgorithm: config.signatureAlgorithm as SamlFormValues["signatureAlgorithm"],
    }),
    ...(config.digestAlgorithm != null && {
      digestAlgorithm: config.digestAlgorithm as SamlFormValues["digestAlgorithm"],
    }),
    ...(config.identifierFormat != null && {
      identifierFormat: config.identifierFormat as SamlFormValues["identifierFormat"],
    }),
  };
}

const samlFormDefaults: Partial<SamlFormValues> = {
  entryPoint: "",
  cert: "",
  wantAssertionsSigned: true,
  authnRequestsSigned: false,
  privateKey: "",
  signatureAlgorithm: "rsa-sha256",
  digestAlgorithm: "sha256",
  identifierFormat: "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
  mappingEmail: "email",
  mappingName: "displayName",
  mappingFirstName: "givenName",
  mappingLastName: "surname",
};

type SamlRegistrationFormProps = {
  preset?: ProviderPreset;
  editProvider?: SamlEditProvider;
  initialConfigMode?: "metadata" | "manual";
};

export default function SamlRegistrationForm({
  preset,
  editProvider,
  initialConfigMode = "manual",
}: SamlRegistrationFormProps) {
  const navigate = useNavigate();
  const currentUser = useAssertCurrentUser();
  const organizationId = currentUser.company.guid;
  const defaultDomain = extractEmailDomain(currentUser.email);

  const isEditing = !!editProvider;

  const initialDomains = editProvider
    ? editProvider.domain.split(",").map((d) => d.trim())
    : defaultDomain
      ? [defaultDomain]
      : [];

  const samlProviderName = preset?.providerName ?? "custom";
  const samlProviderId = generateProviderId(organizationId, "saml", samlProviderName);

  const [formError, setFormError] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>(initialDomains);
  const [domainTouched, setDomainTouched] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm<SamlFormValues>({
    resolver: zodResolver(isEditing ? samlFormSchema : samlFormCreateSchema),
    mode: "onBlur",
    defaultValues: {
      issuer:
        editProvider?.issuer ??
        preset?.defaultValues?.issuer ??
        generateSamlEntityId(organizationId, samlProviderName),
      configMode: initialConfigMode,
      idpMetadataXml: "",
      idpEntityId: "",
      ...samlFormDefaults,
      ...(isEditing
        ? { mappingEmail: "", mappingName: "", mappingFirstName: "", mappingLastName: "" }
        : {
            mappingEmail: preset?.defaultValues?.mappingEmail ?? samlFormDefaults.mappingEmail,
            mappingName: preset?.defaultValues?.mappingName ?? samlFormDefaults.mappingName,
            mappingFirstName:
              preset?.defaultValues?.mappingFirstName ?? samlFormDefaults.mappingFirstName,
            mappingLastName:
              preset?.defaultValues?.mappingLastName ?? samlFormDefaults.mappingLastName,
          }),
      ...samlConfigToFormDefaults(editProvider?.samlConfig),
    },
  });

  const authnRequestsSigned = watch("authnRequestsSigned");
  const acsUrl = `${authBaseUrl}/sso/saml2/sp/acs/${samlProviderId}`;

  const { mutate: registerProvider, isLoading: isRegisteringProvider } = useRegisterSsoProvider({
    onSuccess: async () => {
      toast.success("SSO provider registered");
      await navigate({ to: "/account-management/sso" });
    },
    onError: (error) => {
      setFormError(error.message ?? "Could not register SSO provider");
    },
  });

  const { mutate: updateProvider, isLoading: isUpdatingProvider } = useUpdateSsoProvider({
    onSuccess: async () => {
      toast.success("SSO provider updated");
      await navigate({ to: "/account-management/sso" });
    },
    onError: (error) => {
      setFormError(error.message ?? "Could not update SSO provider");
    },
  });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);

    if (domains.length === 0) {
      setFormError("At least one domain is required");
      return;
    }

    const domainString = domains.join(",");

    if (isEditing) {
      const samlConfig = formValuesToSamlConfig(values, true);
      updateProvider({
        providerId: editProvider.providerId,
        issuer: values.issuer,
        domain: domainString,
        samlConfig,
      });
    } else {
      const samlConfig = formValuesToSamlConfig(values);
      registerProvider({
        providerId: samlProviderId,
        issuer: values.issuer,
        domain: domainString,
        organizationId,
        samlConfig,
        overrideUserInfo: true,
      });
    }
  });

  const isPending = isRegisteringProvider || isUpdatingProvider;

  return (
    <Flex column gap={24}>
      <Flex column gap={8}>
        <Text h1>
          {isEditing
            ? "Edit SAML SSO provider"
            : `Register SAML provider${preset?.name ? `: ${preset.name}` : ""}`}
        </Text>
        <Text description>
          {isEditing
            ? "Update the configuration for this SSO provider."
            : "Configure a SAML 2.0 identity provider for single sign-on."}
        </Text>
      </Flex>

      <Form onSubmit={onSubmit}>
        <Flex column gap={16}>
          <FieldGroup>
            <label>Assertion Consumer Service (ACS) URL</label>
            <Text description>
              Endpoint users are redirected to after authenticating with your identity provider.
              Also known as Single Sign-On URL or Callback URL.
            </Text>
            <CopyableField value={acsUrl} toastMessage="ACS URL copied to clipboard" />
          </FieldGroup>

          <FieldGroup>
            <label>Service Provider Entity ID</label>
            <Text description>
              Unique identifier for the service provider. Also known as Audience URI.
            </Text>
            <CopyableField value={watch("issuer")} toastMessage="Entity ID copied to clipboard" />
          </FieldGroup>

          {/* TODO: Re-enable metadata mode once we handle IdP metadata formats better
          <FieldGroup>
            <label>IdP configuration</label>
            <Select control={control} name="configMode">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="metadata">Upload IdP metadata XML</SelectItem>
                <SelectItem value="manual">Configure manually</SelectItem>
              </SelectContent>
            </Select>
          </FieldGroup>
          */}

          <FieldGroup>
            <label htmlFor="entryPoint">Single sign-on URL</label>
            <Text description>The single sign-on endpoint of your identity provider.</Text>
            <Input
              control={control}
              name="entryPoint"
              placeholder={preset?.placeholders?.entryPoint ?? "https://idp.example.com/sso"}
            />
            {errors.entryPoint && <ErrorText>{errors.entryPoint.message}</ErrorText>}
          </FieldGroup>

          {/* TODO: Re-enable metadata mode once we ensure better-auth handles IdP metadata formats better
          {configMode === "metadata" && (
            <FieldGroup>
              <label htmlFor="idpMetadataXml">IdP metadata XML</label>
              <Text description>Paste the metadata XML exported from your identity provider.</Text>
              <TextAreaInput
                id="idpMetadataXml"
                control={control}
                name="idpMetadataXml"
                placeholder={"<md:EntityDescriptor ...>"}
                minRows={4}
                rules={
                  configMode === "metadata"
                    ? { required: "IdP metadata XML is required" }
                    : undefined
                }
              />
              {errors.idpMetadataXml && <ErrorText>{errors.idpMetadataXml.message}</ErrorText>}
            </FieldGroup>
          )}
          */}

          <FieldGroup>
            <label htmlFor="idpEntityId">
              Identity Provider Identifier{isEditing ? " (leave blank to keep existing)" : ""}
            </label>
            <Text description>
              The unique identifier of your identity provider. Found in your IdP's SAML settings or
              metadata. Also known as Entity ID.
            </Text>
            <Input
              control={control}
              name="idpEntityId"
              placeholder={preset?.placeholders?.idpEntityId ?? "https://idp.example.com/entity-id"}
            />
            {errors.idpEntityId && <ErrorText>{errors.idpEntityId.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="cert">
              Signing certificate{isEditing ? " (leave blank to keep existing)" : ""}
            </label>
            <Text description>The X.509 certificate used to verify SAML responses.</Text>
            {isEditing &&
              editProvider.samlConfig?.certificate?.map((cert, i) => (
                <Text key={i} description>
                  {"error" in cert
                    ? `Current: ${cert.error}`
                    : `Current: ${cert.publicKeyAlgorithm} · expires ${cert.notAfter} · SHA256: ${cert.fingerprintSha256}`}
                </Text>
              ))}
            <Flex gap={8} alignItems="center">
              <Button
                buttonType="secondary"
                icon="upload01"
                type="button"
                width="fit-content"
                onClick={async () => {
                  const result = await pickAndReadPemFile(
                    [
                      ".cer", // Windows/IE certificate format
                      ".crt", // OpenSSL default certificate extension
                      ".pem", // PEM-encoded certificate
                      ".cert", // alternate certificate extension
                      ".der", // DER binary (converted to PEM on read)
                      "application/x-x509-ca-cert", // .crt, .der
                      "application/x-pem-file", // .pem
                      "application/pkix-cert", // .cer
                    ],
                    "Certificate",
                    "certificate",
                  );
                  if (!result) return;
                  if (!result.ok) {
                    toast.error(result.error);
                    return;
                  }
                  setValue("cert", result.pem);
                }}
              >
                Upload certificate
              </Button>
              <Text description>or paste below</Text>
            </Flex>
            <TextAreaInput
              id="cert"
              control={control}
              name="cert"
              placeholder="-----BEGIN CERTIFICATE-----"
              minRows={3}
              onBlur={() => {
                const v = watch("cert");
                if (v) setValue("cert", wrapBareBase64AsPem(v, "certificate"));
              }}
            />
            {errors.cert && <ErrorText>{errors.cert.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label>Allowed email domains</label>
            <Text description>
              Users with email addresses on these domains are forced to use this SSO connection.
              Subdomains are automatically included.
            </Text>
            <TagInput
              values={domains}
              onChange={setDomains}
              placeholder="example.com"
              onBlur={() => setDomainTouched(true)}
            />
            {domainTouched && domains.length === 0 && (
              <ErrorText>At least one domain is required</ErrorText>
            )}
          </FieldGroup>
        </Flex>

        <Collapsible
          title="Advanced settings"
          defaultOpen={false}
          isBordered
          showBottomDivider={false}
          contentWrapperStyles={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 16,
          }}
        >
          <ToggleRow>
            <Flex column gap={2}>
              <label>Require signed assertions</label>
              <Text description>Require the IdP to sign SAML assertions</Text>
            </Flex>
            <Controller
              control={control}
              name="wantAssertionsSigned"
              render={({ field }) => (
                <Toggle checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </ToggleRow>

          <ToggleRow>
            <Flex column gap={2}>
              <label>Sign authentication requests</label>
              <Text description>Sign outgoing authentication requests to the IdP</Text>
            </Flex>
            <Controller
              control={control}
              name="authnRequestsSigned"
              render={({ field }) => (
                <Toggle
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    setValue("authnRequestsSigned", checked, { shouldValidate: true });
                    if (!checked) {
                      setValue("privateKey", "", { shouldValidate: true });
                    }
                  }}
                />
              )}
            />
          </ToggleRow>

          {authnRequestsSigned && (
            <FieldGroup>
              <label htmlFor="privateKey">SP signing private key</label>
              <Text description>PEM-encoded private key for signing AuthnRequests.</Text>
              <Flex gap={8} alignItems="center">
                <Button
                  buttonType="secondary"
                  icon="upload01"
                  type="button"
                  width="fit-content"
                  onClick={async () => {
                    const result = await pickAndReadPemFile(
                      [
                        ".pem", // PEM-encoded key
                        ".key", // OpenSSL default key extension
                        ".p8", // PKCS#8 key
                        ".der", // DER binary (converted to PEM on read)
                        "application/x-pem-file", // .pem
                        "application/pkcs8", // .p8, .key
                      ],
                      "Private key",
                      "private-key",
                    );
                    if (!result) return;
                    if (!result.ok) {
                      toast.error(result.error);
                      return;
                    }
                    setValue("privateKey", result.pem);
                  }}
                >
                  Upload key
                </Button>
                <Text description>or paste below</Text>
              </Flex>
              <TextAreaInput
                id="privateKey"
                control={control}
                name="privateKey"
                placeholder="-----BEGIN PRIVATE KEY-----"
                minRows={3}
                onBlur={() => {
                  const v = watch("privateKey");
                  if (v) setValue("privateKey", wrapBareBase64AsPem(v, "private-key"));
                }}
              />
              {errors.privateKey && <ErrorText>{errors.privateKey.message}</ErrorText>}
            </FieldGroup>
          )}

          <FieldGroup>
            <label>Signature algorithm</label>
            <Select control={control} name="signatureAlgorithm">
              <SelectTrigger>
                <SelectValue placeholder="Default (RSA-SHA256)" />
              </SelectTrigger>
              <SelectContent>
                {signatureAlgorithm.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <FieldGroup>
            <label>Digest algorithm</label>
            <Select control={control} name="digestAlgorithm">
              <SelectTrigger>
                <SelectValue placeholder="Default (SHA256)" />
              </SelectTrigger>
              <SelectContent>
                {digestAlgorithm.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <FieldGroup>
            <label>Name ID format</label>
            <Select control={control} name="identifierFormat">
              <SelectTrigger>
                <SelectValue placeholder="Email address - urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress" />
              </SelectTrigger>
              <SelectContent>
                {identifierFormat.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {identifierFormatLabels[value]} - {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <Flex column gap={8}>
            <Text h3>Attribute mapping</Text>
            <Text description>
              Map SAML assertion attributes to user fields.
              {isEditing && " Leave blank to keep existing mapping."}
            </Text>
          </Flex>

          <FieldGroup>
            <label htmlFor="mappingEmail">Email attribute</label>
            <Input control={control} name="mappingEmail" placeholder="email" />
            {errors.mappingEmail && <ErrorText>{errors.mappingEmail.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="mappingName">Display name attribute</label>
            <Input control={control} name="mappingName" placeholder="displayName" />
            {errors.mappingName && <ErrorText>{errors.mappingName.message}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="mappingFirstName">First name attribute</label>
            <Input control={control} name="mappingFirstName" placeholder="givenName" />
          </FieldGroup>

          <FieldGroup>
            <label htmlFor="mappingLastName">Last name attribute</label>
            <Input control={control} name="mappingLastName" placeholder="surname" />
          </FieldGroup>
        </Collapsible>

        <Flex gap={8}>
          <Button type="submit" loading={isPending} disabled={!isValid || domains.length === 0}>
            {isEditing ? "Save changes" : "Register provider"}
          </Button>
          <Button
            as="a"
            to={isEditing ? "/account-management/sso" : "/account-management/sso/add"}
            buttonType="secondary"
            activeOptions={{ exact: true }}
          >
            Cancel
          </Button>
        </Flex>
        {formError && <ErrorText>{formError}</ErrorText>}
      </Form>
    </Flex>
  );
}
